import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Box,
  FormHelperText,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AddressAutocomplete } from "../AddressAutocomplete";
import api from '@/services/api';


const OpenPatientAddDialog = ({ open, setOpen, fromDataPatienSignupData, setFromDataPatienSignupData, handlePatientAdd }: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fromDataPatienSignupData: any;
  setFromDataPatienSignupData: React.Dispatch<React.SetStateAction<any>>;
  handlePatientAdd: () => Promise<boolean>
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [careNeedsOptions, setCareNeedsOptions] = useState<any[]>([]);
  const [loadingCareNeeds, setLoadingCareNeeds] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadCareNeeds = async () => {
      try {
        setLoadingCareNeeds(true);
        const response = await api.get('/specilities/specility');
        const data = response?.data?.data || response?.data || [];
        setCareNeedsOptions(data);
      } catch (error) {
        console.error('Error fetching care needs:', error);
      } finally {
        setLoadingCareNeeds(false);
      }
    };
    if (open) {
      loadCareNeeds();
    }
  }, [open]);

  const handleAdd = async () => {
    const newErrors: Record<string, string> = {};
    const data = fromDataPatienSignupData || {};

    if (!data.firstName?.trim()) newErrors.firstName = "First name is required";
    if (!data.lastName?.trim()) newErrors.lastName = "Last name is required";
    if (!data.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!data.addressLine1?.trim()) newErrors.addressLine1 = "Street address is required";
    if (!data.state?.trim()) newErrors.state = "State is required";
    if (!data.postalCode?.trim()) newErrors.postalCode = "Postal code is required";
    if (!data.country?.trim()) newErrors.country = "Country is required";
    if (!data.careNeeds || data.careNeeds.length === 0) {
      newErrors.careNeeds = "Please select at least one care need";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    try {
      const success = await handlePatientAdd();
      if (success) {
        setOpen(false);
      }
    } catch (error) {
      console.error("Error adding patient:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handelSetTextSingUp = (value: string, feildName: string) => {
    setFromDataPatienSignupData((pre: any) => ({ ...pre, [feildName]: value }));
    if (errors[feildName]) {
      setErrors(prev => ({ ...prev, [feildName]: '' }));
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Add Patient</DialogTitle>

      <IconButton
        onClick={() => setOpen(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <TextField
          label="First Name*"
          value={fromDataPatienSignupData?.firstName || ''}
          onChange={(e) => handelSetTextSingUp(e.target.value, "firstName")}
          error={!!errors.firstName}
          helperText={errors.firstName}
          fullWidth
          margin="normal"
          variant="outlined"
          placeholder='First Name*'
        />
        <TextField
          label="Last Name*"
          value={fromDataPatienSignupData?.lastName || ''}
          onChange={(e) => handelSetTextSingUp(e.target.value, "lastName")}
          error={!!errors.lastName}
          helperText={errors.lastName}
          fullWidth
          margin="normal"
          variant="outlined"
          placeholder='Last Name*'
        />
        <TextField
          label="Email*"
          value={fromDataPatienSignupData?.email || ''}
          onChange={(e) => handelSetTextSingUp(e.target.value, "email")}
          error={!!errors.email}
          helperText={errors.email}
          fullWidth
          margin="normal"
          variant="outlined"
          placeholder='Email*'
        />
        <AddressAutocomplete
          value={fromDataPatienSignupData?.addressLine1 || ''}
          onChange={(address, components) => {
            setFromDataPatienSignupData((prev: any) => ({
              ...prev,
              addressLine1: address,
              ...(components && {
                ...(components.city && { city: components.city }),
                ...(components.country && { country: components.country }),
                ...(components.state && { state: components.state }),
                ...((components.zipCode || components.postalCode) && {
                  postalCode: components.zipCode || components.postalCode,
                }),
                ...(components.latitude !== undefined && { latitude: components.latitude }),
                ...(components.longitude !== undefined && { longitude: components.longitude }),
              }),
            }));
            setErrors(prev => ({
              ...prev,
              addressLine1: '',
              ...(components && {
                country: components.country ? '' : prev.country,
                state: components.state ? '' : prev.state,
                postalCode: (components.zipCode || components.postalCode) ? '' : prev.postalCode,
              })
            }));
          }}
          placeholder="Start typing a Street Address 1...*"
          addressType="address"
          error={!!errors.addressLine1}
          helperText={errors.addressLine1}
        />
        <TextField
          label="State*"
          value={fromDataPatienSignupData?.state || ''}
          onChange={(e) => handelSetTextSingUp(e.target.value, "state")}
          error={!!errors.state}
          helperText={errors.state}
          fullWidth
          margin="normal"
          variant="outlined"
          placeholder='State*'
        />
        <TextField
          label="Postal Code*"
          value={fromDataPatienSignupData?.postalCode || ''}
          onChange={(e) => handelSetTextSingUp(e.target.value, "postalCode")}
          error={!!errors.postalCode}
          helperText={errors.postalCode}
          fullWidth
          margin="normal"
          variant="outlined"
          placeholder='Postal Code*'
        />
        <TextField
          label="Country*"
          value={fromDataPatienSignupData?.country || ''}
          onChange={(e) => handelSetTextSingUp(e.target.value, "country")}
          error={!!errors.country}
          helperText={errors.country}
          fullWidth
          margin="normal"
          variant="outlined"
          placeholder='Country*'
        />
        <FormControl fullWidth margin="normal" variant="outlined" error={!!errors.careNeeds}>
          <InputLabel id="care-needs-select-label">Select Care Needs*</InputLabel>
          <Select
            labelId="care-needs-select-label"
            id="care-needs-select"
            multiple
            value={fromDataPatienSignupData?.careNeeds || []}
            onChange={(e) => {
              const value = e.target.value as string[];
              setFromDataPatienSignupData((prev: any) => ({
                ...prev,
                careNeeds: value
              }));
              if (errors.careNeeds) {
                setErrors(prev => ({ ...prev, careNeeds: '' }));
              }
            }}
            input={<OutlinedInput label="Select Care Needs*" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((id) => {
                  const name = careNeedsOptions.find(opt => opt._id === id)?.name || id;
                  return <Chip key={id} label={name} size="small" />;
                })}
              </Box>
            )}
          >
            {loadingCareNeeds ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
              </MenuItem>
            ) : (
              careNeedsOptions.map((option) => (
                <MenuItem key={option._id} value={option._id}>
                  <Checkbox checked={(fromDataPatienSignupData?.careNeeds || []).indexOf(option._id) > -1} />
                  <ListItemText primary={option.name} />
                </MenuItem>
              ))
            )}
          </Select>
          {errors.careNeeds && <FormHelperText>{errors.careNeeds}</FormHelperText>}
        </FormControl>
        <DialogActions sx={{ px: 0, pb: 1, pt: 2 }}>
          <Button variant='contained' color='error' sx={{ backgroundColor: '#a0a0a0ff', color: 'white' }} onClick={() => setOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant='contained'
            // color='success'
            sx={{
              backgroundColor: '#7A6FA1',
              color: 'white',
              minWidth: 120
            }}
            onClick={handleAdd}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isSubmitting ? 'Adding...' : 'Add Patient'}
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}

export default OpenPatientAddDialog
